import { useState } from 'react';
import { Box, Card, Flex, Heading, IconButton, Menu, Portal, Tag, Text } from '@chakra-ui/react';
import { LuEllipsisVertical } from 'react-icons/lu';
import { Link } from 'react-router';
import type { CustomerType } from '@customTypes/customer';
import CreatedBy from '@components/CreatedBy';

import ConfirmDisableDialog from './ConfirmDisableDialog';
import { useAuthContext } from '@contexts/auth';

type Props = {
  customer: CustomerType,
  refetch: () => void,
};

const CardContainer = ({ customer }: {
  customer: CustomerType,
}) => {
  const isEnabled = customer.is_enabled;
  
  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      pl={4}
      pr={isEnabled ? 14 : 4}
      py={3}
      display="flex"
      justifyContent="center"
      _hover={{ bg: 'bg.muted' }}
    >
      <Flex
        gap={{ base: 1, md: 2 }}
        flexDirection={{ base: 'column', lg: 'row' }}
        mb={{ base: 1, md: 0 }}
      >
        <Heading
          size="md"
          textDecoration={isEnabled ? 'none' : 'line-through'}
          color={isEnabled ? 'fg.default' : 'fg.error'}
        >
          {customer.name}
        </Heading>
        <Flex gap={1}>
          {customer.tags.map((tag) => (
            <Tag.Root key={tag} size="lg" bg="argos.fg" color="grayscale" fontWeight={600}>
              <Tag.Label>{tag}</Tag.Label>
            </Tag.Root>
          ))}
        </Flex>
      </Flex>
      <Text color="fg.muted">
        {customer.portfolio.name}
      </Text>
      <CreatedBy user={customer.user} createdDate={customer.created_date} />
    </Card.Root>
  );
};

const CustomerCard = ({ customer, refetch }: Props) => {
  const { checkIfHasRole } = useAuthContext();

  const [showConfirmDisableDialog, setShowConfirmDisableDialog] = useState(false);

  const isEnabled = customer.is_enabled;

  const canEditCustomers = checkIfHasRole('CAN_EDIT_CUSTOMERS');
  const canDisableCustomers = checkIfHasRole('CAN_DISABLE_CUSTOMERS');
  const canDetailCustomers = checkIfHasRole('CAN_DETAIL_CUSTOMERS');

  const showCardMenu = canEditCustomers || canDisableCustomers;

  return (
    <Box position="relative">
      {canDetailCustomers ? (
        <Link to={`/painel/clientes/${customer.id}`}>
          <CardContainer customer={customer} />
        </Link>
      ) : (
        <CardContainer customer={customer} />
      )}

      {showConfirmDisableDialog && (
        <ConfirmDisableDialog
          customer={customer}
          handleClose={() => setShowConfirmDisableDialog(false)}
          afterSubmit={refetch}
        />
      )}
      
      {isEnabled && showCardMenu && (
        <Flex
          position="absolute"
          zIndex={10}
          right={3}
          bottom={0}
          height="100%"
          alignItems="center"
        >
          <Menu.Root>
            <Menu.Trigger asChild>
              <IconButton variant="ghost" size="sm">
                <LuEllipsisVertical />
              </IconButton>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  {canEditCustomers && (
                    <Link to={`/painel/clientes/editar/${customer.id}`}>
                      <Menu.Item value="edit">
                        Editar
                      </Menu.Item>
                    </Link>
                  )}
                  {canDisableCustomers && (
                    <Menu.Item
                      value="delete"
                      color="fg.error"
                      _hover={{ bg: "bg.error", color: "fg.error" }}
                      onClick={() => setShowConfirmDisableDialog(true)}
                    >
                      Desabilitar
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>     
        </Flex>
      )}
    </Box>
  );
};

export default CustomerCard;
